import { StablecoinProvider } from './stablecoin.model';
import type { StablecoinActivityFilter } from './report.model';

export enum StablecoinTransferType {
	deposit = 'deposit',
	withdrawalFiat = 'withdrawal_fiat',
	withdrawalCrypto = 'withdrawal_crypto',
	conversion = 'conversion',
	internal = 'internal',
}

export enum StablecoinTransferStatus {
	pendingApproval = 'pending_approval',
	approved = 'approved',
	rejected = 'rejected',
	expired = 'expired',
	canceled = 'canceled',
	submitting = 'submitting',
	processing = 'processing',
	completed = 'completed',
	failed = 'failed',
	/** A workflow rule with outcome `hold` matched; a member of the held stage clears it. */
	onHoldRule = 'on_hold_rule',
	/** Release-time capability gate is pending (e.g. Coinbase KYB in review). */
	onHoldProviderCapability = 'on_hold_provider_capability',
	/** The provider refused the transaction (terminal). */
	blockedProviderTransaction = 'blocked_provider_transaction',
	/** The provider capability is inactive (terminal). */
	blockedCapabilityInactive = 'blocked_capability_inactive',
}

/** Actions the API says the caller may take on a transfer right now (`TransferOut.my_actions`). */
export enum StablecoinTransferAction {
	approve = 'approve',
	reject = 'reject',
	release = 'release',
	clearHold = 'clear_hold',
	cancel = 'cancel',
}

export enum StablecoinApprovalDecisionValue {
	approve = 'approve',
	reject = 'reject',
}

/** Workflow pipeline stage a transfer can sit in. */
export enum StablecoinTransferStage {
	initiate = 'initiate',
	review = 'review',
	approve = 'approve',
	release = 'release',
}

export const TRANSFER_STAGE_LABELS: Record<StablecoinTransferStage, string> = {
	[StablecoinTransferStage.initiate]: 'Initiate',
	[StablecoinTransferStage.review]: 'Review',
	[StablecoinTransferStage.approve]: 'Approve',
	[StablecoinTransferStage.release]: 'Release',
};

/** Per-stage requirements snapshot inside a transfer's stage context. */
export interface StablecoinStageContextStage {
	participants: string[];
	required: number;
	required_approvers: string[];
	matched_rules: string[];
	/** Descriptions of the matched `hold` rules for this stage. */
	holds?: string[];
}

/** One rule hold recorded on a transfer (`stage_context.holds[]`). */
export interface StablecoinStageHold {
	stage: string;
	/** Human description of the matched rule. */
	rule: string;
	cleared_by: string | null;
	cleared_at: string | null;
}

/** Workflow routing snapshot attached to a transfer governed by a workflow. */
export interface StablecoinStageContext {
	order: string[];
	stages: { [stage: string]: StablecoinStageContextStage };
	holds?: StablecoinStageHold[];
}

export interface StablecoinTransfer {
	id: string;
	organization_id: string;
	account_id: string;
	type: StablecoinTransferType;
	status: StablecoinTransferStatus;
	source_asset: string | null;
	source_amount: string | null;
	destination_asset: string | null;
	destination_amount: string | null;
	counterparty_account_id: string | null;
	destination_id: string | null;
	network: string | null;
	provider: StablecoinProvider;
	provider_ref: string | null;
	initiated_by: string | null;
	initiated_by_email: string | null;
	failure_reason: string | null;
	workflow_id: string | null;
	current_stage: StablecoinTransferStage | null;
	stage_context: StablecoinStageContext | null;
	/** Why the transfer is held, blocked or canceled (same value as failure_reason). */
	status_reason?: string | null;
	released_by?: string | null;
	/** The releaser's member email, for display; older APIs omit it. */
	released_by_email?: string | null;
	released_at?: string | null;
	/** ENT-11: the destination is a self-approved third-party Address Book entry (marker not yet cleared). */
	destination_self_approved?: boolean;
	/** Server-computed actions for the caller. Absent on APIs that predate TPMK-8026. */
	my_actions?: StablecoinTransferAction[];
	/** Set when the caller is a stage member but approving now would leave no eligible releaser (ENT-10). */
	reserved_for?: string | null;
	created_at: string;
	submitted_at: string | null;
	completed_at: string | null;
	// TPMK-7618 additions — all optional so APIs that predate them still type-check.
	/** Payment Name given at create. */
	name?: string | null;
	/** Free-text reference (memo) given at create. */
	memo?: string | null;
	/** Display payment id: `PAY-` + 8 upper hex of the id. */
	reference?: string | null;
	/** The account an on-chain payment actually sends from (the linked Coinbase account, or the source itself). */
	sends_from_account_id?: string | null;
	sends_from_display_name?: string | null;
	account_display_name?: string | null;
	counterparty_display_name?: string | null;
	/** Address Book entry name and address for on-chain payments; bank name for withdrawals. */
	destination_name?: string | null;
	destination_address?: string | null;
	target_amount?: string | null;
	fees?: StablecoinTransferFee[];
	/** False while fees are a quote; true once the provider reports the charged fees. */
	fees_final?: boolean;
	tx_hash?: string | null;
	explorer_url?: string | null;
	provider_transfer_id?: string | null;
	paxos_orchestration_id?: string | null;
	failure_code?: string | null;
	/** Set on a failed/expired payment once it has been retried; the new payment's id. */
	retried_by_transfer_id?: string | null;
	/** Initiate · Approve · Release · Execute; Approve is `skipped` when the workflow has none. */
	stage_plan?: StablecoinStagePlanEntry[];
	can_cancel?: boolean;
	can_retry?: boolean;
}

/** One fee line on a transfer (`TransferOut.fees[]`). */
export interface StablecoinTransferFee {
	type: string;
	amount: string;
	asset: string;
}

/** One stage of the workflow a payment follows (`TransferOut.stage_plan[]`). */
export interface StablecoinStagePlanEntry {
	/** initiate | approve | release | execute */
	stage: string;
	label: string;
	/** Member emails who can act on the stage. */
	members: string[];
	skipped: boolean;
}

/** `GET /transfers?view=` — All, the caller's review queue (approve / release / clear-hold), or non-terminal payments. */
export enum StablecoinTransferListView {
	all = 'all',
	readyForReview = 'ready_for_review',
	open = 'open',
}

/** List filter for `GET /transfers`: the report filter plus the TPMK-7618 `view`. */
export interface StablecoinTransferListFilter extends StablecoinActivityFilter {
	view?: StablecoinTransferListView | null;
}

/** `POST /transfers/bulk-approvals` body. Bulk approve never releases (release needs MFA). */
export interface StablecoinBulkApprovalRequest {
	transfer_ids: string[];
	decision: StablecoinApprovalDecisionValue;
	comment: string | null;
}

/** One item of the bulk-approvals response; each runs the normal approval path. */
export interface StablecoinBulkApprovalResult {
	id: string;
	ok: boolean;
	error: string | null;
}

export interface StablecoinBulkApprovalResponse {
	results: StablecoinBulkApprovalResult[];
}

export interface StablecoinTransferCreate {
	organization_id?: string | null;
	account_id: string;
	type: StablecoinTransferType;
	asset: string;
	amount: string;
	destination_asset?: string | null;
	counterparty_account_id?: string | null;
	destination_id?: string | null;
	network?: string | null;
}

export interface StablecoinApprovalDecision {
	approver_subject: string;
	/** The approver's member email, for display; null when they're no longer a member, absent on older APIs. */
	approver_email?: string | null;
	decision: StablecoinApprovalDecisionValue;
	comment: string | null;
	created_at: string;
}

/** One approval request on a transfer. stage is null for legacy single-policy approvals. */
export interface StablecoinApprovalState {
	stage: StablecoinTransferStage | null;
	required_count: number;
	status: string;
	initiated_by: string | null;
	expires_at: string | null;
	decisions: StablecoinApprovalDecision[];
}

export interface StablecoinApprovalPolicy {
	id: string;
	organization_id: string;
	auto_approve_under: string | null;
	required_approvals: number;
	allowlist_only: boolean;
	created_at: string;
}

export interface TransferStatusDisplay {
	label: string;
	cssClass: 'status-pending' | 'status-active' | 'status-success' | 'status-failed' | 'status-muted';
}

/** Customer status labels (TPMK-7618 § E). The list shows `approved` as "Ready to release" (see transferListStatusDisplay). */
export const TRANSFER_STATUS_DISPLAY: Record<StablecoinTransferStatus, TransferStatusDisplay> = {
	[StablecoinTransferStatus.pendingApproval]: { label: 'Pending Approval', cssClass: 'status-pending' },
	[StablecoinTransferStatus.approved]: { label: 'Approved', cssClass: 'status-active' },
	[StablecoinTransferStatus.rejected]: { label: 'Rejected', cssClass: 'status-failed' },
	[StablecoinTransferStatus.expired]: { label: 'Expired', cssClass: 'status-muted' },
	[StablecoinTransferStatus.canceled]: { label: 'Cancelled', cssClass: 'status-muted' },
	[StablecoinTransferStatus.submitting]: { label: 'Processing', cssClass: 'status-active' },
	[StablecoinTransferStatus.processing]: { label: 'Processing', cssClass: 'status-active' },
	[StablecoinTransferStatus.completed]: { label: 'Completed', cssClass: 'status-success' },
	[StablecoinTransferStatus.failed]: { label: 'Failed', cssClass: 'status-failed' },
	[StablecoinTransferStatus.onHoldRule]: { label: 'On hold (rule)', cssClass: 'status-pending' },
	[StablecoinTransferStatus.onHoldProviderCapability]: { label: 'On hold — compliance', cssClass: 'status-pending' },
	[StablecoinTransferStatus.blockedProviderTransaction]: { label: 'Blocked — provider', cssClass: 'status-failed' },
	[StablecoinTransferStatus.blockedCapabilityInactive]: { label: 'Blocked — entity not active', cssClass: 'status-failed' },
};

/** The list's short label for an approved payment. */
export const APPROVED_LIST_LABEL: string = 'Ready to release';

/** Customer type labels. Backend types map to the three payment types: internal / withdrawal_crypto (on-chain) / withdrawal_fiat. */
export const TRANSFER_TYPE_LABELS: Record<StablecoinTransferType, string> = {
	[StablecoinTransferType.deposit]: 'Deposit',
	[StablecoinTransferType.withdrawalFiat]: 'Withdrawal',
	[StablecoinTransferType.withdrawalCrypto]: 'On-Chain Transfer',
	[StablecoinTransferType.conversion]: 'Conversion',
	[StablecoinTransferType.internal]: 'Internal Transfer',
};

/** Type-dot colors (design: payments-workflow-engine mockup). Withdrawal is purple. */
export const TRANSFER_TYPE_DOT_COLORS: Record<StablecoinTransferType, string> = {
	[StablecoinTransferType.deposit]: '#12a54d',
	[StablecoinTransferType.withdrawalFiat]: '#9b51e0',
	[StablecoinTransferType.withdrawalCrypto]: '#627eea',
	[StablecoinTransferType.conversion]: '#8a919e',
	[StablecoinTransferType.internal]: '#1b6ef3',
};

/** The payment types the list's type filter offers, in display order. */
export const PAYMENT_LIST_TYPES: StablecoinTransferType[] = [
	StablecoinTransferType.internal,
	StablecoinTransferType.withdrawalCrypto,
	StablecoinTransferType.withdrawalFiat,
];

/** Statuses a payment never leaves; the list stops polling once every visible payment is in one. */
export const TERMINAL_TRANSFER_STATUSES: StablecoinTransferStatus[] = [
	StablecoinTransferStatus.completed,
	StablecoinTransferStatus.rejected,
	StablecoinTransferStatus.canceled,
	StablecoinTransferStatus.expired,
	StablecoinTransferStatus.failed,
	StablecoinTransferStatus.blockedProviderTransaction,
	StablecoinTransferStatus.blockedCapabilityInactive,
];

export const CANCELABLE_STATUSES: StablecoinTransferStatus[] = [
	StablecoinTransferStatus.pendingApproval,
	StablecoinTransferStatus.approved,
	StablecoinTransferStatus.onHoldRule,
	StablecoinTransferStatus.onHoldProviderCapability,
];

/** Statuses the "Needs approval" queue lists: awaiting a decision, or held by a rule a stage member can clear. */
export const NEEDS_ACTION_STATUSES: StablecoinTransferStatus[] = [StablecoinTransferStatus.pendingApproval, StablecoinTransferStatus.onHoldRule];

/**
 * Generic, Compliance-reviewable copy for compliance holds and blocks (KYB-INACT-1/3), used when the API sends no
 * mapped `status_reason`. Never names a reason code, screening result or the destination.
 */
export const TRANSFER_COMPLIANCE_COPY: Partial<Record<StablecoinTransferStatus, string>> = {
	[StablecoinTransferStatus.onHoldProviderCapability]:
		'On hold until the paying entity’s Coinbase account is cleared. It resumes automatically; no action is needed from you.',
	[StablecoinTransferStatus.blockedProviderTransaction]: 'This payment can’t be completed. Contact Trovata support for help.',
	[StablecoinTransferStatus.blockedCapabilityInactive]: 'This payment can’t be completed. Contact Trovata support for help.',
};
